<template>
  <DxPopup
    :visible="getVisible"
    @hidden="onPopupHide"
    @shown="onShown"
    :drag-enabled="false"
    :hide-on-outside-click="false"
    :show-close-button="false"
    :show-title="true"
    :width="'95%'"
    :height="700"
    :title="'Create Release & Bulk Issue'"
    :wrapper-attr="{
      class: 'css-v2 gc-popup gc-popup-release-and-issue-bulk',
    }"
    ref="gc-popup"
  >
    <DxToolbarItem
      v-for="(item, index) in getToolbarItems"
      :key="index"
      :widget="item.widget"
      :location="item.location"
      :visible="item.visible"
      :toolbar="item.position"
      :options="item.options"
    />

    <div class="css-v2 gc-popup-form">
      <div class="gc-popup-form-content">
        <DxForm
          :form-data="model"
          labelLocation="left"
          ref="formInstance"
          :validationGroup="validationGroup"
          :show-required-mark="false"
          @content-ready="onFormContentReady"
        >
          <DxGroupItem :col-count="2">
            <DxGroupItem :col-count="1">
              <!-- Date In  -->
              <DxItem
                data-field="DateIn"
                :editor-type="'dxDateBox'"
                format="dd MMM yyyy"
                :editor-options="{
                  displayFormat: 'dd MMM yyyy',
                  min: DateMin,
                  max: DateMax,
                  valueChangeEvent: 'input',
                  dateOutOfRangeMessage: getDateOutOfRangeMessage,
                }"
              >
                <DxLabel text="Date" />
              </DxItem>

              <!-- Work Order -->
              <DxItem
                data-field="WorkOrderCode"
                :editor-type="'dxSelectBox'"
                :editor-options="getWorkOrderEditorOptions"
              >
                <DxLabel text="Work Order" />
              </DxItem>

              <!-- Product Select -->
              <DxItem
                data-field="ProductCode"
                :editor-type="'dxSelectBox'"
                :editor-options="getProductEditorOptions"
              >
                <DxLabel text="Product" />
              </DxItem>

              <!-- Stock UM -->
              <DxItem
                data-field="ProductUM"
                :editor-type="'dxSelectBox'"
                :editor-options="getUMEditorOptions"
              >
                <DxLabel text="UM" />
              </DxItem>

              <!-- Conversion -->
              <DxItem
                data-field="ConversionFactor"
                :editor-type="'dxNumberBox'"
                cssClass="gc-conversion-field"
                :editor-options="{
                  readOnly: true,
                  tabIndex: -1,
                }"
              >
                <DxLabel text="Conversion" />
              </DxItem>

              <!-- Production UM Qty -->
              <DxItem
                data-field="ProductionUMQty"
                :editor-type="'dxNumberBox'"
                format="##,###,###,###,##0.0000"
                :editor-options="getProductionUMQtyEditorOptions"
              >
                <DxLabel text="Production UM Qty" />
              </DxItem>

              <!-- Stock UM Qty -->
              <DxItem
                data-field="StockUMQty"
                :editor-type="'dxNumberBox'"
                format="##,###,###,###,##0.0000"
                :editor-options="getStockUMQtyEditorOptions"
              >
                <DxLabel text="Stock UM Qty" />
              </DxItem>
            </DxGroupItem>
            <DxGroupItem :col-count="1">
              <!-- Batch Code -->
              <DxItem
                data-field="BatchCode"
                :editor-type="'dxTextBox'"
                :editor-options="getBatchCodeEditorOptions"
                :validationRules="getBatchCodeValidationRules"
              >
                <DxLabel text="Batch" />
              </DxItem>

              <!-- Proportionately Issue -->
              <DxItem
                data-field="ProportionatelyIssue"
                :editor-type="'dxCheckBox'"
                :editor-options="{
                  value: model.ProportionatelyIssue,
                  onValueChanged: (e) => {
                    model.ProportionatelyIssue = e.value;
                    if (e.event) this.updateLineStockUMQty();
                  },
                }"
              >
                <DxLabel text="Proportionately Issue" />
              </DxItem>

              <!-- Complete Work Order -->
              <DxItem
                data-field="CompleteWorkOrder"
                :editor-type="'dxCheckBox'"
                :editor-options="{
                  value: model.CompleteWorkOrder,
                }"
              >
                <DxLabel text="Complete Work Order" />
              </DxItem>

              <!-- Comments -->
              <DxItem
                data-field="Comments"
                :editor-type="'dxTextArea'"
                :editor-options="{
                  value: model.Comments,
                  height: 110,
                  onKeyDown: this.onCommentsKeyDown,
                }"
                :validationRules="getCommentsValidationRules"
              >
                <DxLabel text="Comments" />
              </DxItem>
            </DxGroupItem>
          </DxGroupItem>
        </DxForm>
      </div>
    </div>

    <div class="data-grid-details">
      <dx-data-grid
        :height="gridHeight"
        :ref="'dataGridRef'"
        :columnChooser="{
          enabled: false,
        }"
        :pager="{ visible: false }"
        :columns="getColumns"
        :data-source="getWorkOrderMaterialDataSource"
        :focusedRowEnabled="true"
        keyExpr="id"
        @editing-start="onEditingStart"
        @cell-prepared="onCellPrepared"
        @editor-preparing="onEditorPreparing"
        @selection-changed="onSelectionChanged"
      >
        <DxEditing
          :allow-updating="true"
          :mode="'cell'"
          :changes="changes"
          :confirm-delete="false"
          :allow-deleting="false"
        />

        <DxSelection
          :select-all-mode="'allPages'"
          :show-check-boxes-mode="'always'"
          :mode="'multiple'"
          :allowSelectAll="false"
        />

        <DxMasterDetail :enabled="true" template="masterDetailTemplate" />
        <template #masterDetailTemplate="{ data: lineItem }">
          <dx-data-grid
            :columns="getChildGridColumns"
            :data-source="getBatchDataSourceConfig(lineItem)"
            :columnChooser="{
              enabled: false,
            }"
            :searchPanel="{
              visible: false,
            }"
            :pager="{ visible: false }"
            :ref="'master-details-' + lineItem.data.id"
            :key="'master-details-' + lineItem.data.id"
            :elementAttr="{ class: 'internal-grid' }"
            :row-alternation-enabled="true"
            :focusedRowEnabled="true"
            @selectionChanged="onBatchSelectionChanged"
            @editor-preparing="onBatchEditorPreparing"
            @contentReady="onBatchContentReady"
            keyExpr="id"
            :twoWayBindingEnabled="true"
          >
            <DxSelection
              :select-all-mode="'allPages'"
              :show-check-boxes-mode="'always'"
              :mode="'multiple'"
              :allowSelectAll="false"
            />

            <DxEditing
              :allow-deleting="false"
              :allow-updating="true"
              :mode="'cell'"
              :confirm-delete="false"
            />

            <template #Decimal="{ data }">
              <GcDecimal :dataValue="data" :format="data.column.format" />
            </template>
          </dx-data-grid>
        </template>
      </dx-data-grid>
    </div>
  </DxPopup>
</template>

<script>
import { DxForm, DxItem, DxLabel, DxGroupItem } from "devextreme-vue/form";
import { ProductionReleaseService } from "../../_services/api/production/production-release.service";
import { getActiveAccountPeriod } from "../../_helpers/default-value-rules/default-value-rules";
import { getFactorByCode } from "../../_store/helpers";
import validationEngine from "devextreme/ui/validation_engine";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";
import ArrayStore from "devextreme/data/array_store";
import { WorkOrderService } from "../../_services/api/production/work-order.service";
import { formatNumber } from "devextreme/localization";
import { MaterialIssuesService } from "../../_services/api/production/material-issues.service";
import { backwardTabbing, forwardTabbing } from "../../_helpers/ui/keyboard";
import { mapGetters } from "vuex";

const batchOutstandingExceedMsg = `Outstanding quantity already fulfilled. Do you want to continue selecting the batch?`;

export default {
  components: {
    DxForm,
    DxItem,
    DxLabel,
    DxGroupItem,
  },
  props: {
    popupConfig: {
      type: Object,
      default: () => {},
    },
    showLoading: {
      type: Function,
      default: () => {},
    },
    isPopupShow: {
      type: Boolean,
      default: false,
    },
    onHide: {
      type: Function,
      default: () => [],
    },
  },
  data() {
    return {
      model: {
        DateIn: new Date(),
        WorkOrderCode: null,
        ProductCode: null,
        ProductUM: null,
        ConversionFactor: null,
        StockUMConversion: 0,
        ProductionUMQty: 0,
        StockUMQty: 0,
        StockUM: null,
        IsBatchProduct: "No",
        OutstandingQuantity: 0,
        BatchCode: "Non-Batch",
        ProportionatelyIssue: false,
        CompleteWorkOrder: false,
        BranchCode: null,
        Comments: "",
        WorkOrderStatus: undefined,
        PalletNo: undefined,
      },
      DateMin: undefined,
      DateMax: undefined,
      formInstance: null,
      IsCommentsRequired: false,
      validationGroup: "productionRelease",
      gridHeight: "auto",
      changes: [],
      workOrderMaterialDataSource: new ArrayStore({
        key: "id",
        data: [],
      }),
      LineCellErrorVisible: false,
      BatchGridInstance: null,
      WorkOrderInstance: "Core",
      IssueType: null,
      IssueTypeRecordId: null,
      batchSelectionPromise: null,
    };
  },
  async created() {
    this.model.BranchCode = this.popupConfig.dataModel.BranchCode;

    await this.getWorkOrderMaterialList(
      this.popupConfig.dataModel.WorkOrderCode
    );
    await this.$store.dispatch("screenFactors/getFactors", [514, 570]);
    const factor = getFactorByCode(514);

    if (factor && factor.Factorc === "Y") {
      this.model.ProportionatelyIssue = true;
    }

    const commentFactor = getFactorByCode(570);
    if (commentFactor && commentFactor.Factorc === "Y") {
      this.IsCommentsRequired = true;
    }

    const datePeriod = await getActiveAccountPeriod();
    if (datePeriod) {
      this.DateMin = datePeriod.DateMin;
      this.DateMax = datePeriod.DateMax;
    }

    await this.getDefaultIssueType();

    this.$nextTick(() => {
      this.formInstance = this.$refs.formInstance.instance;
      this.model.WorkOrderCode = this.popupConfig.dataModel.WorkOrderCode;

      this.focusOnFormField("DateIn");

      setTimeout(() => {
        this.showLoading(false);
      }, 100);
    });
  },
  methods: {
    async getDefaultIssueType() {
      try {
        const response = await MaterialIssuesService.getDefaultIssueType(
          this.WorkOrderInstance
        );

        if (response && response.data && response.data.value.length > 0) {
          const defaultIssueType = response.data.value[0];
          this.IssueType = defaultIssueType.MaterialIssueTypeName;
          this.IssueTypeRecordId = defaultIssueType.RecordId;
        }
      } catch (error) {
        this.displayServerErrorOnTemplate(
          error,
          "Unable to load default issue type."
        );
      }
    },
    /**
     * Handle the popup hide event
     */
    async onPopupHide() {
      this.$store.dispatch("hotKey/setHotKeyPopupMode", false);
      this.$emit("update:isPopupShow", false);
      this.onHide();
    },
    onShown() {
      this.$store.dispatch("hotKey/setHotKeyPopupMode", true);

      this.$nextTick(() => {
        this.showLoading(true);
      });
    },
    async onPopupClose() {
      const confirmLeaving = await this.$confirm(
        "Do you want to discard the bulk creation?",
        "Unsaved changes"
      );
      if (confirmLeaving) {
        this.$emit("update:isPopupShow", false);
      }
    },
    async validateForm() {
      this.$nextTick(() => {
        const group = validationEngine.getGroupConfig(this.validationGroup);
        if (group) {
          validationEngine.validateGroup(this.validationGroup);
        }
      });
    },

    async onSaveProductionRelease() {
      await this.focusOutFromEditableCells();
      if (this.batchSelectionPromise) {
        await this.batchSelectionPromise;
      }
      await this.$nextTick();

      if (
        this.model.DateIn < new Date(this.DateMin) ||
        this.model.DateIn > new Date(this.DateMax)
      ) {
        await this.$alert(this.getDateOutOfRangeMessage, "Validation Error");
        this.focusOnFormField("DateIn");
        return;
      }

      let isFormValid = { isValid: true };
      const group = validationEngine.getGroupConfig(this.validationGroup);
      if (
        group &&
        (this.model.StockUMQty !== 0 || this.model.ProductionUMQty !== 0)
      ) {
        isFormValid = await validationEngine.validateGroup(
          this.validationGroup
        );
      }

      let brokenRule = "";
      if (!isFormValid.isValid)
        isFormValid.brokenRules.forEach((x) => {
          brokenRule += x.message + "\n";
        });

      if (brokenRule) {
        await this.$alert(brokenRule, "Validation Error");
        return;
      }

      // Check if at least one material is valid
      const isMaterialValid = this.workOrderMaterialDataSource._array.some(
        (item) => item.StockUMQty !== 0 || item.Completed === "Y"
      );

      if (
        !this.model.CompleteWorkOrder &&
        (this.model.StockUMQty === 0 || this.model.ProductionUMQty === 0) &&
        !isMaterialValid
      ) {
        this.$alert(
          "Please enter a Release Quantity or ensure at least one material has an Issue Quantity before saving this transaction.",
          "Validation Error"
        );
        return;
      } else {
        // Save continue
        const materialPayload = await this.getMaterialPayloadData();

        if (materialPayload.ReconciliationErrors.length > 0) {
          this.$alert(
            "The Batch Quantity for the following line(s) do not reconcile with the Issue Quantity. " +
              "Please re-check the batch allocation before saving:\n" +
              materialPayload.ReconciliationErrors.join("\n"),
            "Validation Error"
          );
          return;
        }

        if (
          materialPayload &&
          materialPayload.MaterialIssueDetails.length > 0
        ) {
          // Proceed with saving the material payload
          this.saveMaterialIssue(materialPayload);
        } else {
          // Save Production Release only
          this.saveProductionRelease();
        }
      }
    },

    async saveMaterialIssue(payload) {
      this.showLoading(true);

      try {
        const response = await MaterialIssuesService.saveMaterialIssue(payload);

        if (
          response &&
          (this.model.StockUMQty === 0 || this.model.ProductionUMQty === 0)
        ) {
          this.$notify(
            `The Issue ${response.data.IssueNo} for Work Order ${this.model.WorkOrderCode} has saved successfully.`,
            "success"
          );
          this.$nextTick(() => {
            this.completeSaveAndUpdate();
          });
        } else if (
          this.model.StockUMQty != 0 &&
          this.model.ProductionUMQty != 0
        ) {
          this.saveProductionRelease(response);
        } else {
          this.$nextTick(() => {
            this.$notify(
              `Material issues have been successfully saved.`,
              "success"
            );
            this.completeSaveAndUpdate();
          });
        }
      } catch (error) {
        if (error && error.data && error.data.Errors) {
          if (
            Array.isArray(error.data.Errors) &&
            error.data.Errors[0].MessageType != undefined
          ) {
            this.displayLineCellError(
              error.data.Errors[0].Message,
              "Server Error"
            );
          } else {
            this.displayServerErrorOnTemplate(
              error,
              "Error saving material issue"
            );
          }
        } else {
          this.displayLineCellError(
            `Unable to save material issue. Please try again later.`,
            "Error"
          );
        }
      } finally {
        if (this.model.StockUMQty === 0 || this.model.ProductionUMQty === 0) {
          this.showLoading(false);
        }
      }
    },

    async saveProductionRelease(materialResponse) {
      this.showLoading(true);
      const payload = this.getCoreVersionPayload();
      try {
        const response = await ProductionReleaseService.saveProductionRelease(
          "Save_ProductionRelease",
          payload
        );

        if (response && !materialResponse) {
          this.$notify(
            `The Release ${response.data.ReleaseCode} for Work Order ${this.model.WorkOrderCode} has saved successfully.`,
            "success"
          );
        } else if (response && materialResponse) {
          this.$notify(
            `The Release ${response.data.ReleaseCode} and Issue ${materialResponse.data.IssueNo} for Work Order ${this.model.WorkOrderCode} have saved successfully.`,
            "success"
          );
        } else {
          this.$notify(
            `Production release has been successfully saved.`,
            "success"
          );
        }

        this.$nextTick(() => {
          this.completeSaveAndUpdate();
        });
      } catch (error) {
        if (error && error.data && error.data.Errors) {
          if (
            Array.isArray(error.data.Errors) &&
            error.data.Errors[0].MessageType != undefined
          ) {
            this.displayLineCellError(
              error.data.Errors[0].Message,
              "Server Error"
            );
          } else {
            this.displayServerErrorOnTemplate(
              error,
              "Error saving production release"
            );
          }
        } else {
          this.displayLineCellError(
            `Unable to save production release. Please try again later.`,
            "Error"
          );
        }
      } finally {
        this.showLoading(false);
      }
    },

    async getMaterialPayloadData() {
      const payload = {
        IsAlmondcoMaterialIssue: this.WorkOrderInstance === "Almondco",
        // IssueNo: 0,
        // Username: "string",
        // WorkOrderCode: 0,
        BranchCode: this.model.BranchCode,
        DateIn: this.model.DateIn.toSaveDate().replace("Z", ""),
        MaterialIssueDetails: [],
        ReconciliationErrors: [],
      };

      // Load material issue details either qty or completed
      const materialIssueDetails = await this.workOrderMaterialDataSource.load({
        filter: [[["StockUMQty", "<>", 0], "or", ["Completed", "=", "Y"]]],
      });

      payload.MaterialIssueDetails = await Promise.all(
        materialIssueDetails.map(async (item) => {
          const isBatchControlled = item.IsBatchProduct != "No";
          const batchData = isBatchControlled
            ? await item.ChildDataSource.load({
                filter: [
                  ["IssuedQty", "<>", 0],
                  "and",
                  ["IsIncluded", "=", true],
                ],
              })
            : [];

          // For batch-controlled lines, the batch selection is the source of
          // truth for the issued quantity — not the separately-maintained
          // StockUMQty rollup, which can drift out of sync with it.
          const batchTotal = batchData
            .reduce((sum, batchItem) => sum + (batchItem.IssuedQty || 0), 0)
            .roundNumber(4);

          if (
            isBatchControlled &&
            (item.StockUMQty !== 0 || item.Completed === "Y") &&
            batchTotal !== item.StockUMQty
          ) {
            payload.ReconciliationErrors.push(
              `${item.MaterialCode}: Issue Qty ${item.StockUMQty}, Batch Qty ${batchTotal}`
            );
          }

          return {
            WorkOrderCode: item.WorkOrderCode,
            ProcessCode: item.ProcessCode,
            ProductCode: item.ProductCode,
            IssueQty: isBatchControlled ? batchTotal : item.StockUMQty,
            Comment: item.Comment,
            Completed: item.Completed,
            ProductionUMReleaseQty: item.ProductionUMReleaseQty,
            CardNo: item.BinCardNo,
            SeasonYear: item.SeasonYear,
            StorageLocationRecordId: item.StorageLocationRecordId,
            IssueType: this.IssueType,
            IssueTypeRecordId: this.IssueTypeRecordId,
            IsBatchProduct: item.IsBatchProduct,
            MaterialIssueBatches: batchData.map((batchItem) => {
              return {
                SupplierCode: batchItem.SupplierCode,
                BatchNo: batchItem.BatchNo,
                BatchQty: batchItem.IssuedQty,
              };
            }),
          };
        })
      );

      return payload;
    },

    getCoreVersionPayload() {
      let payload = {
        ReleaseCode: 0,
        DateIn: this.model.DateIn.toSaveDate().replace("Z", ""),
        BranchCode: this.model.BranchCode,
        ProductionReleaseLines: [],
      };

      const releaseLine = {
        Comments: this.model.Comments,
        WorkOrderCode: this.model.WorkOrderCode,
        WorkOrderStatus: this.model.WorkOrderStatus,
        ProductCode: this.model.ProductCode,
        ItemQty: this.model.StockUMQty,
        ProductionReleaseLineBatches:
          this.model.IsBatchProduct.toLocaleLowerCase() === "manual"
            ? [
                {
                  BatchNo: this.model.BatchCode,
                  BatchQty: this.model.StockUMQty,
                },
              ]
            : [],
        PalletNo: this.model.PalletNo,
        ProductionUM: this.model.ProductUM,
        ProductionUMReleaseQty: this.model.ProductionUMQty,
        ProductionUMConversion: this.model.ConversionFactor,
        BackflushStock: "N",
        WorkOrderComplete: this.model.CompleteWorkOrder ? "Y" : "N",
      };

      payload.ProductionReleaseLines.push(releaseLine);

      return payload;
    },

    completeSaveAndUpdate() {
      this.$nextTick(() => {
        this.onPopupHide();
        this.$emit("onRefreshDataList");
        this.$emit("onRefreshWorkOrderHeader");
      });
    },

    async getProductPrimaryProductionUM() {
      await ProductionReleaseService.getPrimaryProductionUM({
        filter: `ProductCode eq '${this.model.ProductCode}' and BranchCode eq '${this.model.BranchCode}' and IsProductionUM eq 'Y' and IsPrimaryProductionUM eq 'Y'`,
      }).then((res) => {
        if (res.data.value.length > 0) {
          this.model.ProductUM = res.data.value[0].UnitOfMeasureCode;
        }
      });
    },
    async getPrimaryProductDetails() {
      await ProductionReleaseService.getPrimaryProductDetails(
        this.model.WorkOrderCode
      ).then((res) => {
        if (res.data.value.length > 0) {
          this.model.ProductCode = res.data.value[0].ProductCode;

          this.model.BranchCode = res.data.value[0].BranchCode;
          this.model.ConversionFactor = res.data.value[0].ConversionFactor;
          this.model.IsBatchProduct = res.data.value[0].IsBatchProduct;
          this.model.StockUM = res.data.value[0].StockUM;
          this.model.StockUMConversion = res.data.value[0].StockUMConversion;

          const IsBatchProduct = this.model.IsBatchProduct;

          if (IsBatchProduct.toLowerCase() !== "manual") {
            if (this.model.IsBatchProduct === "No") {
              this.model.BatchCode = "Non-Batch";
            } else this.model.BatchCode = IsBatchProduct;
          } else {
            this.model.IsBatchProduct = IsBatchProduct;
            this.model.BatchCode = undefined;
          }

          this.$nextTick(() => {
            this.validateForm();
          });
        }
      });
    },

    focusOnFormField(dataField) {
      this.formInstance.getEditor(dataField).focus();
    },

    updateProductSelection(e) {
      const component = e.component; // the dxSelectBox instance
      const productData = component
        .option("items")
        .find((item) => item.ProductCode === e.value);

      if (productData) {
        this.model.BatchCode = null;
        this.model.ProductCode = productData.ProductCode;
        this.model.IsBatchProduct = productData.IsBatchProduct;
        const IsBatchProduct = productData.IsBatchProduct;

        if (IsBatchProduct.toLowerCase() !== "manual") {
          if (this.model.IsBatchProduct === "No") {
            this.model.BatchCode = "Non-Batch";
          } else this.model.BatchCode = IsBatchProduct;
        } else {
          this.model.IsBatchProduct = IsBatchProduct;
        }
        this.getProductPrimaryProductionUM();
      } else {
        this.model.ProductCode = null;
        this.model.BatchCode = "Non-Batch";
        this.model.IsBatchProduct = "No";
      }

      this.$nextTick(() => {
        setTimeout(() => {
          this.validateForm();
        }, 100);
      });
    },

    async onWorkOrderSelectionChanged(e) {
      if (e.selectedItem) {
        this.model.WorkOrderCode = e.selectedItem.Code;
        this.model.WorkOrderStatus = e.selectedItem.Status;
        this.model.OutstandingQuantity = (
          e.selectedItem.ProductionUMQty - e.selectedItem.ProductionUMActualQty
        ).roundNumber(4);
        this.model.BranchCode = e.selectedItem.BranchCode;
        await this.getPrimaryProductDetails();
        await this.getProductPrimaryProductionUM();
      } else {
        this.model.WorkOrderCode = null;
        this.model.ProductCode = null;
        this.model.BranchCode = null;
        this.model.OutstandingQuantity = 0;
      }
    },

    async onProductValueChanged(e) {
      if (!e.event) return;
      if (e.value && e.previousValue) {
        const confirmChange = await this.$confirm(
          "All the input will be reset, are you sure want to change the Product?",
          "Confirmation"
        );
        if (!confirmChange) {
          this.model.ProductCode = e.previousValue;
          this.focusOnFormField("ProductCode");
          return;
        } else {
          this.model.ProductCode = e.value;
          this.model.ProductionUMQty = 0;
          this.model.StockUMQty = 0;
          this.model.Comments = "";
          await this.updateLineStockUMQty(true);

          this.getDataGridRef.clearSelection();
          this.$nextTick(() => {
            this.focusOnFormField("ProductUM");
          });
        }
      }

      this.$nextTick(() => {
        this.updateProductSelection(e);
      });
    },

    async onUMValueChanged(e) {
      if (!e.event) return;
      if (e.value) {
        this.model.ProductUM = e.value;
      } else {
        this.model.ProductUM = null;
        this.model.ConversionFactor = 0;
      }

      this.focusOnFormField("ProductionUMQty");
    },

    async onUMSelectionChanged(e) {
      if (e.selectedItem) {
        this.model.ProductUM = e.selectedItem.UnitOfMeasureCode;
        this.model.ConversionFactor = e.selectedItem.ConversionFactor;

        this.model.StockUMQty = (
          this.model.ProductionUMQty *
          (this.model.ConversionFactor / this.model.StockUMConversion)
        ).roundNumber(4);
      }

      this.$nextTick(() => {
        this.updateLineStockUMQty(this.model.StockUMQty === 0);
      });
    },

    async onProductionUMQtyValueChanged(e) {
      if (!e.event) return;

      let confirmQty = true;
      if (e.value > this.model.OutstandingQuantity) {
        confirmQty = await this.$confirm(
          "The quantity you have entered will over Release against this work order. Are you sure you wish to do this?",
          "Confirmation"
        );

        if (!confirmQty) {
          this.model.ProductionUMQty = e.previousValue;
          this.focusOnFormField("ProductionUMQty");
          return;
        } else {
          this.focusOnFormField("StockUMQty");
        }
      }

      this.model.ProductionUMQty = e.value;
      this.model.StockUMQty = (
        e.value *
        (this.model.ConversionFactor / this.model.StockUMConversion)
      ).roundNumber(4);

      this.$nextTick(() => {
        this.updateLineStockUMQty(this.model.StockUMQty === 0);
      });
    },

    async onStockUMQtyValueChanged(e) {
      if (!e.event) return;

      let confirmQty = true;
      const ProductionUMQty = (
        e.value *
        (this.model.StockUMConversion / this.model.ConversionFactor)
      ).roundNumber(4);

      if (ProductionUMQty > this.model.OutstandingQuantity) {
        confirmQty = await this.$confirm(
          "The quantity you have entered will over Release against this work order. Are you sure you wish to do this?",
          "Confirmation"
        );

        if (!confirmQty) {
          this.model.StockUMQty = e.previousValue;
          this.focusOnFormField("StockUMQty");
          return;
        } else {
          //   this.focusOnFormField("ProductionUMQty"); // Next Field
        }
      }

      this.model.StockUMQty = e.value;
      this.model.ProductionUMQty = ProductionUMQty;

      this.$nextTick(() => {
        this.updateLineStockUMQty(this.model.StockUMQty === 0);
      });
    },

    onFormContentReady() {
      this.$nextTick(() => {
        this.setModelItemHeights();
      });
    },

    displayServerErrorOnTemplate(err, msg) {
      this.$store.dispatch("ui/setServerError", {
        error: err,
        title: msg,
      });
    },

    // eslint-disable-next-line no-unused-vars
    async updateLineStockUMQty(clear = false) {
      await this.$nextTick();
      if (this.model.ProportionatelyIssue) {
        const rowKeys = [];

        const lines = await this.workOrderMaterialDataSource.load({
          filter: [
            ["IsBatchProduct", "=", "No"],
            "and",
            ["IsFixedMaterial", "=", false],
          ],
        });

        this.getDataGridRef.deselectRows(lines.map((x) => x.id));

        for (const item of lines) {
          if (this.model.ProportionatelyIssue && item.IsBatchProduct === "No") {
            const StockUMQty = (
              item.MaterialQuantity * this.model.StockUMQty
            ).roundNumber(4);

            this.workOrderMaterialDataSource.update(item.id, {
              StockUMQty: StockUMQty,
            });
            if (StockUMQty !== 0) rowKeys.push(item.id);
          }
        }

        this.getDataGridRef.selectRows(rowKeys, true);
        this.$nextTick(() => {
          this.getDataGridRef.refresh(true);
        });
      }
    },

    async onCommentsKeyDown(e) {
      if (forwardTabbing(e)) {
        e.event.preventDefault();
        const gridInstance = this.getDataGridRef;

        var gridDS = gridInstance.getDataSource();
        var loadOptions = gridDS.loadOptions();
        gridDS
          .store()
          .load(loadOptions)
          .done((loadedItems) => {
            if (loadedItems.length > 0) {
              let rowIndex = 0;
              let dataField = "StockUMQty";
              let data = loadedItems[rowIndex];

              if (data.IsBatchProduct !== "No") {
                dataField = "Completed";
              }

              if (data.IsBatchProduct !== "No" && data.Completed === "Y") {
                if (data.IsBatchProduct !== "No") {
                  this.moveIntoFirstFieldInBatch(data);
                } else {
                  var gridDS = gridInstance.getDataSource();
                  var loadOptions = gridDS.loadOptions();
                  gridDS
                    .store()
                    .load(loadOptions)
                    .done((loadedItems) => {
                      let rowIndex = loadedItems.findIndex(
                        (x) => x.id == data.id
                      );
                      let nextRowIndex = rowIndex + 1;
                      let nextRow = loadedItems[nextRowIndex];

                      if (nextRow) {
                        const dataField =
                          nextRow.IsBatchProduct === "No"
                            ? "StockUMQty"
                            : "Completed";
                        const rowIndex = this.getDataGridRef.getRowIndexByKey(
                          nextRow.id
                        );
                        this.focusOnEditableCell(rowIndex, dataField);
                      }
                    });
                }
              } else {
                const cellElement = gridInstance.getCellElement(
                  rowIndex,
                  dataField
                );
                gridInstance.focus(cellElement);
                gridInstance.editCell(rowIndex, dataField);
              }
            }
          });
      }
    },

    // Data Grid Functions
    setModelItemHeights() {
      this.$nextTick(() => {
        const popupHeight = document.querySelector(
          ".gc-popup-release-and-issue-bulk .dx-overlay-content.dx-popup-normal.dx-resizable"
        ).clientHeight;
        const toolBarHeight = document.querySelector(
          ".gc-popup-release-and-issue-bulk .dx-toolbar.dx-widget.dx-visibility-change-handler.dx-collection.dx-popup-title"
        ).clientHeight;

        const headerHeight = document.querySelector(
          ".gc-popup-release-and-issue-bulk .dx-popup-content .css-v2.gc-popup-form"
        ).clientHeight;

        this.gridHeight = popupHeight - toolBarHeight - headerHeight - 12;

        this.resizeDetailsGrid();
      });
    },
    resizeDetailsGrid() {
      this.$nextTick(() => {
        const grid = this.getDataGridRef;
        if (grid) {
          grid.updateDimensions();
        }
      });
    },
    async getWorkOrderMaterialList(code) {
      this.workOrderMaterialDataSource.clear();

      await this.$nextTick();

      const res = await WorkOrderService.getWorkOrderMaterialList(code, {
        filter: `Completed eq 'N'`,
      });
      if (!res) return;

      const records = res.data.value || [];

      // Collect all async insert + batch processing promises
      const tasks = records.map(async (element) => {
        element.StockUMQty = 0;
        element.ChildDataSource = new ArrayStore({
          key: "id",
          data: [],
        });

        const inserted = await this.workOrderMaterialDataSource.insert({
          ...element,
          IsEditable: false,
          id: element.RecordId,
        });

        if (inserted.IsBatchProduct !== "No") {
          await this.getProductBatchLines(inserted);
        }
      });

      // Wait for all tasks to finish
      await Promise.all(tasks);

      // Ensure Vue DOM updates are flushed before continuing
      await this.$nextTick();
    },
    onEditingStart(e) {
      const rowData = e.data;
      if (rowData) {
        if (e.column.dataField) {
          if (
            rowData.IsBatchProduct !== "No" &&
            (e.column.dataField === "StockUMQty" ||
              e.column.dataField === "Completed")
          ) {
            e.cancel = true;
          }
        }
      }
    },
    onCellPrepared(e) {
      if (e.rowType !== "data") return;

      if (e.rowType === "data" && e.column.command === "expand") {
        if (e.data.IsBatchProduct === "No") {
          if (e.cellElement && e.cellElement.classList) {
            e.cellElement.classList.remove("dx-datagrid-expand");
            e.cellElement.childNodes[0].classList.remove(
              "dx-datagrid-group-closed"
            );
          }
        }
      }

      if (e.column.command === "select") {
        if (e.data.IsBatchProduct !== "No") {
          if (e.cellElement && e.cellElement.classList) {
            e.cellElement.classList.add("gc-remove-selection");
            e.cellElement.children[0].classList.add("dx-state-disabled");
          }
        }
      }
    },
    onSelectionChanged(e) {
      if (e.currentSelectedRowKeys.length > 0) {
        for (const key of e.currentSelectedRowKeys) {
          this.workOrderMaterialDataSource.byKey(key).then((item) => {
            if (item && item.IsBatchProduct === "No") {
              let StockUMQty;
              if (this.model.ProportionatelyIssue) {
                StockUMQty = (
                  item.MaterialQuantity * this.model.StockUMQty
                ).roundNumber(4);
              } else {
                StockUMQty = item.OutstandingQty;
              }
              this.workOrderMaterialDataSource
                .update(key, { StockUMQty: StockUMQty })
                .then(() => {
                  const rowIndex = this.getDataGridRef.getRowIndexByKey(key);
                  if (rowIndex !== -1)
                    this.getDataGridRef.repaintRows([rowIndex]);
                });
            }
          });
        }
      }

      if (e.currentDeselectedRowKeys.length > 0) {
        for (const key of e.currentDeselectedRowKeys) {
          this.workOrderMaterialDataSource.byKey(key).then((item) => {
            if (item) {
              if (item.IsBatchProduct !== "No") {
                const batchGrid = this.getBatchGridInstance(item.id);
                if (batchGrid) {
                  batchGrid.deselectAll();
                }
              } else {
                this.workOrderMaterialDataSource.update(key, {
                  StockUMQty: 0,
                  Completed: "N",
                });
              }
            }
          });
        }
      }
    },
    onEditorPreparing(e) {
      if (e.parentType !== "searchPanel") {
        const { dataField, editorOptions, row } = e;
        // Only wrap handlers if not in the search panel
        if (!editorOptions) return;

        const wrapHandler = (handlerName) => {
          const original = editorOptions[handlerName];
          if (typeof original === "function") {
            editorOptions[handlerName] = (event) => {
              original(event, {
                column: { dataField },
                data: row && row.data ? row.data : {},
                rowIndex: row && row.rowIndex ? row.rowIndex : -1,
              });
            };
          }
        };

        // Wrap each handler if it exists
        [
          "onValueChanged",
          "onItemClick",
          "onKeyDown",
          "onOpened",
          "onFocusIn",
        ].forEach(wrapHandler);
      }
    },

    updateLineErrorVisibility() {
      this.LineCellErrorVisible = !this.LineCellErrorVisible;
    },
    async displayLineCellError(errorMessage, errorTitle = "Validation Error") {
      if (!this.LineCellErrorVisible) {
        this.updateLineErrorVisibility();
        await this.$alert(errorMessage, errorTitle);
        this.updateLineErrorVisibility();
      }
    },
    focusOnEditableCell(rowIndex, col) {
      const column = col ? col : "StockUMQty";
      const dataGrid = this.getDataGridRef;
      const cellElement = dataGrid.getCellElement(rowIndex, column);
      dataGrid.focus(cellElement);
      setTimeout(() => {
        dataGrid.editCell(rowIndex, column);
      }, 10);
    },

    // Batch
    getBatchGridInstance(ParentID) {
      if (this.$refs["master-details-" + ParentID]) {
        this.BatchGridInstance =
          this.$refs["master-details-" + ParentID].instance;
        return this.BatchGridInstance;
      } else return false;
    },
    focusOnBatchEditableCell(currentRowData, column = "IssuedQty") {
      if (!currentRowData) return;
      const batchDataGrid = this.getBatchGridInstance(currentRowData.ParentID);
      const rowIndex = batchDataGrid.getRowIndexByKey(currentRowData.id);
      const cellElement = batchDataGrid.getCellElement(rowIndex, column);
      batchDataGrid.focus(cellElement);

      setTimeout(() => {
        batchDataGrid.editCell(rowIndex, column);
      }, 10);
    },

    async onBatchSelectionChanged(e) {
      this.batchSelectionPromise = this.processBatchSelectionChanged(e);
      await this.batchSelectionPromise;
    },

    async processBatchSelectionChanged(e) {
      const batchGrid = e.component;

      if (e.currentDeselectedRowKeys.length > 0) {
        for (const key of e.currentDeselectedRowKeys) {
          const item = await batchGrid.byKey(key);
          if (!item) continue;

          const parentItem = await this.getDataGridRef.byKey(item.ParentID);
          if (!parentItem) continue;

          // IsIncluded and IssuedQty must clear together — a gap here lets a
          // save slip through with a real IssueQty but no batch attribution.
          parentItem.ChildDataSource.update(key, {
            IsIncluded: false,
          });

          const rowIndex = batchGrid.getRowIndexByKey(key);
          const batchLine = await batchGrid.byKey(key);
          if (batchLine.IssuedQty != 0) {
            if (rowIndex !== -1) {
              batchGrid.cellValue(rowIndex, "IssuedQty", 0);
            }
            await this.$nextTick();
            batchGrid.saveEditData();
          }
        }
      }

      if (e.currentSelectedRowKeys.length > 0) {
        for (const key of e.currentSelectedRowKeys) {
          batchGrid.byKey(key).then(async (item) => {
            if (item) {
              this.getDataGridRef
                .byKey(item.ParentID)
                .then(async (parentItem) => {
                  if (parentItem) {
                    const rowIndex = batchGrid.getRowIndexByKey(key);
                    setTimeout(() => {
                      batchGrid.byKey(key).then(async (batchLine) => {
                        if (
                          batchLine.RemainingQty != 0 &&
                          batchLine.IssuedQty == 0 &&
                          batchLine.IsIncluded !== true
                        ) {
                          if (rowIndex !== -1) {
                            let StockUMQty = 0;
                            if (this.model.ProportionatelyIssue) {
                              StockUMQty = (
                                parentItem.MaterialQuantity *
                                this.model.StockUMQty
                              ).roundNumber(4);

                              if (StockUMQty > batchLine.RemainingQty) {
                                StockUMQty = batchLine.RemainingQty;
                              } else {
                                if (StockUMQty > parentItem.StockUMQty) {
                                  StockUMQty =
                                    StockUMQty - parentItem.StockUMQty;
                                } else {
                                  const confirmQty = await this.$confirm(
                                    batchOutstandingExceedMsg,
                                    "Confirmation"
                                  );

                                  if (confirmQty) {
                                    StockUMQty = batchLine.RemainingQty;
                                  } else {
                                    StockUMQty = 0;
                                  }
                                }
                              }
                            } else {
                              if (
                                parentItem.StockUMQty >=
                                parentItem.OutstandingQty
                              ) {
                                const confirmQty = await this.$confirm(
                                  batchOutstandingExceedMsg,
                                  "Confirmation"
                                );

                                if (confirmQty) {
                                  StockUMQty = batchLine.RemainingQty;
                                } else {
                                  StockUMQty = 0;
                                }
                              } else {
                                const parentOutstandingQtyRemaining = (
                                  parentItem.OutstandingQty -
                                  parentItem.StockUMQty
                                ).roundNumber(4);

                                StockUMQty =
                                  parentOutstandingQtyRemaining >
                                  batchLine.RemainingQty
                                    ? batchLine.RemainingQty
                                    : parentOutstandingQtyRemaining;
                              }
                            }

                            batchGrid.cellValue(
                              rowIndex,
                              "IssuedQty",
                              StockUMQty
                            );

                            parentItem.ChildDataSource.update(key, {
                              IsIncluded: StockUMQty !== 0,
                            });

                            await this.$nextTick();
                            this.$nextTick(() => {
                              batchGrid.saveEditData();
                            });
                          }
                        }
                      });
                    }, 100);
                  }
                });
            }
          });
        }
      }
    },

    onBatchEditorPreparing(e) {
      if (e.parentType !== "searchPanel") {
        const { dataField, editorOptions, row } = e;
        // Only wrap handlers if not in the search panel
        if (!editorOptions) return;

        const wrapHandler = (handlerName) => {
          const original = editorOptions[handlerName];
          if (typeof original === "function") {
            editorOptions[handlerName] = (event) => {
              original(event, {
                column: { dataField },
                data: row && row.data ? row.data : {},
                rowIndex: row && row.rowIndex ? row.rowIndex : -1,
              });
            };
          }
        };

        // Wrap each handler if it exists
        [
          "onValueChanged",
          "onItemClick",
          "onKeyDown",
          "onOpened",
          "onFocusIn",
        ].forEach(wrapHandler);
      }
    },

    onBatchContentReady(e) {
      const dataGrid = e.component;
      const dataSource = dataGrid.getDataSource();
      const items = dataSource.items();

      let selectedRowIndexs = [];
      for (const item of items) {
        if (item.IsIncluded) {
          selectedRowIndexs.push(item.id);
        }
      }

      if (selectedRowIndexs.length > 0) {
        dataGrid.selectRows(selectedRowIndexs, true);
      }
    },

    async getProductBatchLines(line) {
      const rowData = { ...line };

      rowData.ChildDataSource = new ArrayStore({
        key: "id",
        data: [],
      });
      try {
        const batchData = await MaterialIssuesService.getAllBatchNo(
          `ItemCode eq '${rowData.MaterialCode}'`,
          {
            select: [
              "ItemCode",
              "BranchCode",
              "SourceTransactionType",
              "SourceTransactionNo",
              "SourceTransactionLine",
              "SourceTransactionProcessCode",
              "CompanyCode",
              "SupplierCode",
              "SupplierName",
              "BatchNo",
              "AllocatedQty",
              "AvailableQty",
              "OnHand",
              "UseByDate",
              "CurrentTransactionAllocatedQty",
              "UseByShelfLife",
              "BestBeforeShelfLife",
              "BestBeforeDate",
              "PickedUM",
              "PickedUMConversion",
              "AvailablePickedUMQty",
              "ExpiryDate",
              "ExpiryDateString",
              "ExpiryMethod",
              "ManufacturedDate",
            ],
          },
          this.model.BranchCode
        );

        if (batchData && batchData.data && batchData.data.value) {
          for (const batch of batchData.data.value) {
            let InTransactionQty = 0;
            const items = await this.workOrderMaterialDataSource.load({
              filter: [
                ["ProductCode", "=", rowData.MaterialCode],
                "and",
                ["id", "<>", rowData.id],
              ],
            });

            if (items && items.length > 0) {
              for (const item of items) {
                if (item.ChildDataSource && item.ChildDataSource._array) {
                  for (const child of item.ChildDataSource._array) {
                    if (child.BatchNo === batch.BatchNo) {
                      InTransactionQty += child.IssuedQty;
                    }
                  }
                }
              }
            }

            rowData.ChildDataSource.insert({
              BatchNo: batch.BatchNo,
              ExpiryDate: batch.BestBeforeDate || batch.UseByDate,
              UseByDate: batch.UseByDate,
              BestBeforeDate: batch.BestBeforeDate,
              AvailableQty: batch.AvailableQty,
              OnHand: batch.OnHand,
              AllocatedQty: batch.AllocatedQty,
              UseByShelfLife: batch.UseByShelfLife,
              BestBeforeShelfLife: batch.BestBeforeShelfLife,
              SupplierCode: batch.SupplierCode,
              SupplierName: batch.SupplierName,
              ItemCode: batch.ItemCode,
              BranchCode: batch.BranchCode,
              InTransactionQty: InTransactionQty,
              RemainingQty: batch.AvailableQty - InTransactionQty,
              IssuedQty: 0,
              ParentID: rowData.id,
            });
          }

          this.workOrderMaterialDataSource.update(rowData.id, {
            ChildDataSource: rowData.ChildDataSource,
          });
        }

        this.$nextTick(() => {
          this.getDataGridRef.saveEditData();
          this.getDataGridRef.refresh();
        });
      } catch (error) {
        this.displayServerErrorOnTemplate(
          error,
          "Error fetching batch data. Please try again later."
        );
      }
    },
    getBatchDataSourceConfig(lineItem) {
      return lineItem.data.ChildDataSource;
    },
    onBatchIssuedQtyKeyDown(e, cellInfo) {
      if (e.event) {
        let data = cellInfo.data;
        let gridInstance = this.getDataGridRef;
        let batchGridInstance = this.getBatchGridInstance(data.ParentID);
        let items = batchGridInstance.getDataSource().items();
        let rowIndex = batchGridInstance.getRowIndexByKey(data.id);

        var gridDS = gridInstance.getDataSource();
        var loadOptions = gridDS.loadOptions();

        if (forwardTabbing(e)) {
          if (rowIndex == items.length - 1) {
            e.event.preventDefault();
            batchGridInstance.option("focusedRowIndex", -1);

            gridDS
              .store()
              .load(loadOptions)
              .done((loadedItems) => {
                let parentRowIndex = loadedItems.findIndex(
                  (x) => x.id == data.ParentID
                );

                let nextRowIndex = parentRowIndex + 1;
                let nextRow = loadedItems[nextRowIndex];

                e.component.blur();

                setTimeout(() => {
                  if (nextRow) {
                    let dataField =
                      nextRow.IsBatchProduct === "No"
                        ? "StockUMQty"
                        : "Complete";
                    let rowIndex = gridInstance.getRowIndexByKey(nextRow.id);
                    this.focusOnEditableCell(rowIndex, dataField);
                  }
                }, 10);
              });
          } else {
            e.event.preventDefault();
            this.focusOnBatchEditableCell(items[rowIndex + 1]);
          }
        } else if (backwardTabbing(e)) {
          if (rowIndex === 0) {
            e.event.preventDefault();
            gridInstance.option("focusedRowIndex", -1);
            let parentRowIndex = gridInstance.getRowIndexByKey(data.ParentID);

            this.focusOnEditableCell(parentRowIndex, "Completed");
          }
        }
      }
    },

    moveIntoFirstFieldInBatch(data) {
      this.getDataGridRef.expandRow(data.id).then(() => {
        setTimeout(() => {
          this.$nextTick(() => {
            const batchGrid = this.getBatchGridInstance(data.id);
            if (batchGrid) {
              const rowIndex = 0;
              const cellElement = batchGrid.getCellElement(
                rowIndex,
                "IssuedQty"
              );
              batchGrid.focus(cellElement);
              batchGrid.editCell(rowIndex, "IssuedQty");
            }
          });
        }, 100);
      });
    },

    moveIntoLastFieldInBatch(e, prevItem) {
      let gridInstance = this.getDataGridRef;
      gridInstance.expandRow(prevItem.id).then(() => {
        let batchGridInstance = this.getBatchGridInstance(prevItem.id);
        let items = batchGridInstance.getDataSource().items();

        if (items.length > 0) {
          // e.event.preventDefault();

          let dataField = "IssuedQty";

          if (batchGridInstance) {
            let cellElement = batchGridInstance.getCellElement(
              items.length - 1,
              dataField
            );
            if (cellElement) {
              let rowIndex = batchGridInstance.getRowIndexByKey(
                items[items.length - 1].id
              );
              batchGridInstance.focus(cellElement);
              setTimeout(() => {
                batchGridInstance.editCell(rowIndex, dataField);
              }, 1);
            }
          }
        }
      });
    },

    async focusOutFromEditableCells() {
      var dataGrid = this.getDataGridRef;
      const activeElement = document.activeElement;
      if (activeElement) {
        activeElement.blur();
        if (dataGrid) dataGrid.saveEditData();
      }
    },
  },
  computed: {
    ...mapGetters({ getHotKeySave: "hotKey/getHotKeySave" }),
    getDataGridRef() {
      return this.$refs.dataGridRef.instance;
    },
    getVisible() {
      if (this.isPopupShow) return this.isPopupShow;
      else return false;
    },
    getToolbarItems() {
      return [
        {
          widget: "dxButton",
          location: "after",
          toolbar: "top",
          visible: true,
          options: {
            text: "Save",
            stylingMode: "contained",
            type: "default",
            onClick: this.onSaveProductionRelease,
          },
        },
        {
          widget: "dxButton",
          location: "after",
          toolbar: "top",
          visible: true,
          options: {
            text: "",
            icon: "close",
            stylingMode: "text",
            type: "default",
            onClick: this.onPopupClose,
          },
        },
      ];
    },
    getWorkOrderEditorOptions() {
      return {
        readOnly: true,
        tabIndex: -1,
        dataSource: ProductionReleaseService.dataSourceConfig(
          "api/WorkOrderList",
          {
            filter: [
              ["FriendlyStatus", "=", "Awaiting Production"],
              "or",
              ["FriendlyStatus", "=", "In Production"],
              "or",
              ["FriendlyStatus", "=", "Review"],
            ],
            sort: "Code desc",
            select: [
              "Code",
              "FriendlyStatus",
              "WorkOrderCodeSearchText",
              "ProductCode",
              "ProductDescription",
              "BranchCode",
              "ProductionUMActualQty",
              "ProductionUMQty",
              "Status",
            ],
          }
        ),
        displayExpr: "Code",
        valueExpr: "Code",
        searchTimeout: 10,
        searchEnabled: true,
        noDataText: "No eligible Work Orders available.",
        searchExpr: [
          "WorkOrderCodeSearchText",
          "ProductCode",
          "ProductDescription",
        ],
        showClearButton: true,
        dropDownOptions: {
          width: 300,
        },
        onSelectionChanged: this.onWorkOrderSelectionChanged,
      };
    },
    getProductEditorOptions() {
      return {
        dataSource: ProductionReleaseService.dataSourceConfig(
          "api/ProductionReleaseProductList",
          {
            filter: [
              ["WorkOrderCode", "=", parseInt(this.model.WorkOrderCode)],
            ],
            key: "RecordId",
            keyType: {
              RecordId: "Guid",
            },
            paginate: true,
          }
        ),
        displayExpr: "ProductDescription",
        valueExpr: "ProductCode",
        searchEnabled: true,
        searchExpr: ["ProductCode", "ProductDescription"],
        showClearButton: true,
        onFocusIn: (e) => {
          setTimeout(() => {
            e.event.target.select();
          }, 100);
        },
        onValueChanged: this.onProductValueChanged,

        itemTemplate: (data) => {
          return ` <div class="custom-auto-complete" id="${data.ProductCode}" >
                      <span class="auto-complete-primary">
                        ${data.ProductCode}
                      </span>
                      <span class="record-secondary gc-grid-code">
                        ${data.ProductDescription}
                      </span>
                    </div>`;
        },
      };
    },
    getUMEditorOptions() {
      return {
        dataSource: ProductionReleaseService.dataSourceConfig(
          "api/ProductUnitofMeasure",
          {
            filter: [
              ["ProductCode", "=", `${this.model.ProductCode}`],
              "and",
              ["BranchCode", "=", `${this.model.BranchCode}`],
              "and",
              ["IsProductionUM", "=", "Y"],
            ],
            paginate: true,
          }
        ),
        displayExpr: "UnitOfMeasureCode",
        valueExpr: "UnitOfMeasureCode",
        showClearButton: true,
        searchEnabled: true,
        onValueChanged: this.onUMValueChanged,
        onSelectionChanged: this.onUMSelectionChanged,
        onFocusIn: (e) => {
          setTimeout(() => {
            e.event.target.select();
          }, 100);
        },
      };
    },
    getProductionUMQtyEditorOptions() {
      return {
        min: 0,
        format: "##,###,###,###,##0.0000",
        onFocusIn: (e) => {
          this.$nextTick(() => {
            e.event.target.select();
          });
        },
        onValueChanged: this.onProductionUMQtyValueChanged,
      };
    },
    getStockUMQtyEditorOptions() {
      return {
        min: 0,
        format: "##,###,###,###,##0.0000",
        onFocusIn: (e) => {
          setTimeout(() => {
            e.event.target.select();
          }, 100);
        },
        onValueChanged: this.onStockUMQtyValueChanged,
      };
    },

    getBatchCodeEditorOptions() {
      return {
        readOnly: this.model.IsBatchProduct.toLowerCase() !== "manual",
        tabIndex: this.model.IsBatchProduct.toLowerCase() !== "manual" ? -1 : 0,
        validationRules:
          this.model.IsBatchProduct.toLowerCase() === "manual"
            ? [
                {
                  type: "required",
                  message: "Batch is required",
                },
              ]
            : [],
      };
    },
    getBatchCodeValidationRules() {
      return this.model.IsBatchProduct.toLowerCase() === "manual"
        ? [
            {
              type: "required",
              message: "Batch is required",
            },
          ]
        : [];
    },

    getCommentsValidationRules() {
      return this.IsCommentsRequired
        ? [
            {
              type: "required",
              message: "Comments are required",
            },
          ]
        : [];
    },

    // Grid Functions
    getColumns() {
      return [
        {
          caption: "",
          dataField: "",
          type: "selection",
          fixed: true,
          fixedPosition: "left",
          width: 30,
          minWidth: 30,
          editorOptions: {
            onValueChanged: (e, lineData) => {
              if (e.event) {
                const item = lineData.data;
                if (item.IsBatchProduct !== "No") {
                  this.$nextTick(() => {
                    if (item.StockUMQty === 0 && item.Completed === "N") {
                      this.getDataGridRef.deselectRows([item.id]);
                    } else {
                      this.getDataGridRef.selectRows([item.id], true);
                    }
                  });
                } else {
                  if (e.value) {
                    if (this.model.ProportionatelyIssue) {
                      const StockUMQty = (
                        item.MaterialQuantity * this.model.StockUMQty
                      ).roundNumber(4);
                      this.workOrderMaterialDataSource.update(item.id, {
                        StockUMQty: StockUMQty,
                      });
                    } else {
                      this.workOrderMaterialDataSource.update(item.id, {
                        StockUMQty: item.OutstandingQty,
                      });
                    }
                  } else {
                    this.workOrderMaterialDataSource.update(item.id, {
                      StockUMQty: 0,
                      Completed: "N",
                    });
                  }
                }
              }
            },
          },
        },
        {
          caption: "Process",
          dataField: "ProcessCode",
          width: ColumnWidth.ProcessCode.width,
          minWidth: ColumnWidth.ProcessCode.minWidth,
          alignment: "left",
          allowEditing: false,
        },
        {
          caption: "Material",
          dataField: "MaterialCode",
          width: ColumnWidth.Code.width,
          minWidth: ColumnWidth.Code.minWidth,
          allowEditing: false,
        },
        {
          caption: "Description",
          dataField: "MaterialDescription",
          minWidth: ColumnWidth.Description.minWidth,
          allowEditing: false,
        },
        {
          caption: "Outstanding Qty",
          dataField: "OutstandingQty",
          width: ColumnWidth.Quantity4DecimalPoints.width,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          allowEditing: false,
          format: "##,###,###,###,##0.0000",
        },
        {
          caption: "Issued Qty",
          dataField: "StockUMQty",
          width: ColumnWidth.Quantity4DecimalPoints.width,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          format: "##,###,###,###,##0.0000",
          cssClass: "edit-cell-numberbox-field",
          allowSorting: false,
          editorOptions: {
            format: "##,###,###,###,##0.0000",
            valueChangeEvent: "input",
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onKeyDown: (e, lineData) => {
              const rowIndex = this.getDataGridRef.getRowIndexByKey(
                lineData.data.id
              );
              let gridInstance = this.getDataGridRef;
              var gridDS = gridInstance.getDataSource();
              var loadOptions = gridDS.loadOptions();
              if (backwardTabbing(e) && rowIndex != -1 && rowIndex === 0) {
                e.event.preventDefault();
                this.$nextTick(() => {
                  this.focusOnFormField("Comments");
                });
              } else if (backwardTabbing(e)) {
                gridDS
                  .store()
                  .load(loadOptions)
                  .done((loadedItems) => {
                    let index = loadedItems.findIndex(
                      (x) => x.id == lineData.data.id
                    );
                    if (index != -1) {
                      let prevLine = loadedItems[index - 1];
                      if (prevLine.IsBatchProduct != "No") {
                        e.event.preventDefault();
                        gridInstance.option("focusedRowIndex", -1);
                        gridInstance.expandRow(prevLine.id).then(() => {
                          // Prev Items is Batch
                          setTimeout(() => {
                            this.moveIntoLastFieldInBatch(e, prevLine);
                          }, 100);
                        });
                      }
                    }
                  });
              }
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            if (value !== 0)
              this.getDataGridRef.selectRows([currentRowData.id], true);
            else this.getDataGridRef.deselectRows([currentRowData.id]);
            newData.StockUMQty = value;

            return newData;
          },
        },
        {
          caption: "Complete",
          dataField: "Completed",
          editorType: "dxCheckBox",
          width: 70,
          dataType: "boolean",
          calculateCellValue: (data) => {
            return data.Completed === "Y" ? true : false;
          },
          editorOptions: {
            tabIndex: -1,
            onKeyDown: (e, lineData) => {
              let gridInstance = this.getDataGridRef;
              let gridDS = gridInstance.getDataSource();
              let loadOptions = gridDS.loadOptions();
              if (forwardTabbing({ event: e })) {
                if (lineData.data.IsBatchProduct !== "No") {
                  e.preventDefault();

                  this.moveIntoFirstFieldInBatch(lineData.data);
                } else {
                  e.preventDefault();
                  gridDS
                    .store()
                    .load(loadOptions)
                    .done((loadedItems) => {
                      const rowIndex = loadedItems.findIndex(
                        (x) => x.id == lineData.data.id
                      );
                      let nextRowIndex = rowIndex + 1;
                      let nextRow = loadedItems[nextRowIndex];

                      if (nextRow) {
                        const dataField =
                          nextRow.IsBatchProduct === "No"
                            ? "StockUMQty"
                            : "Completed";
                        const rowIndex = this.getDataGridRef.getRowIndexByKey(
                          nextRow.id
                        );
                        this.focusOnEditableCell(rowIndex, dataField);
                      }
                    });
                }
              }

              if (backwardTabbing({ event: e })) {
                const rowIndex = this.getDataGridRef.getRowIndexByKey(
                  lineData.data.id
                );
                if (rowIndex === 0 && lineData.data.IsBatchProduct !== "No") {
                  e.preventDefault();
                  this.$nextTick(() => {
                    this.focusOnFormField("Comments");
                  });
                } else if (lineData.data.IsBatchProduct !== "No") {
                  gridDS
                    .store()
                    .load(loadOptions)
                    .done((loadedItems) => {
                      let index = loadedItems.findIndex(
                        (x) => x.id == lineData.data.id
                      );
                      if (index != -1) {
                        e.preventDefault();
                        let prevLine = loadedItems[index - 1];
                        if (prevLine.IsBatchProduct != "No") {
                          gridInstance.option("focusedRowIndex", -1);
                          gridInstance.expandRow(prevLine.id).then(() => {
                            // Prev Items is Batch
                            setTimeout(() => {
                              this.moveIntoLastFieldInBatch(e, prevLine);
                            }, 100);
                          });
                        } else {
                          const prevIndex =
                            this.getDataGridRef.getRowIndexByKey(prevLine.id);
                          if (prevIndex != -1) {
                            const dataField = "Completed";
                            this.focusOnEditableCell(prevIndex, dataField);
                          }
                        }
                      }
                    });
                }
              }
            },
          },
          setCellValue: async (newData, value) => {
            newData.Completed = value ? "Y" : "N";
            return newData;
          },
        },
      ];
    },
    getWorkOrderMaterialDataSource() {
      return this.workOrderMaterialDataSource._array;
    },

    getDateOutOfRangeMessage() {
      return (
        "Date should be within the valid range of " +
        new Date(this.DateMin).toShortDateString() +
        " to " +
        new Date(this.DateMax).toShortDateString()
      );
    },

    // Batch Grid
    getChildGridColumns() {
      return [
        {
          caption: "Batch",
          dataField: "BatchNo",
          minWidth: ColumnWidth.BatchNo.minWidth,
          width: ColumnWidth.BatchNo.width,
          allowEditing: false,
        },
        {
          caption: "Supplier",
          dataField: "SupplierCode",
          // minWidth: ColumnWidth.SupplierCode.minWidth,
          width: "auto",
          minWidth: 150,
          cellTemplate: (data, info) => {
            return (data.innerHTML = `<div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${info.data.SupplierName}
                        </span> <br />
                      <span class="record-secondary gc-grid-code">
                        ${info.data.SupplierCode}
                      </span>
                    </div>`);
          },
          allowEditing: false,
        },
        {
          caption: "Use By / Best Before",
          dataField: "ExpiryDate",
          dataType: "date",
          minWidth: ColumnWidth.Date.minWidth,
          width: ColumnWidth.Date.width,
          format: "dd MMM yyyy",
          allowEditing: false,
        },
        {
          caption: "Available",
          dataField: "AvailableQty",
          dataType: "number",
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          width: ColumnWidth.Quantity4DecimalPoints.width,
          format: "##,###,###,###,##0.0000",
          cellTemplate: "Decimal",
          allowEditing: false,
        },
        {
          caption: "In Transaction",
          dataField: "InTransactionQty",
          dataType: "number",
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          width: ColumnWidth.Quantity4DecimalPoints.width,
          format: "##,###,###,###,##0.0000",
          cellTemplate: "Decimal",
          allowEditing: false,
        },
        {
          caption: "Remaining",
          dataField: "RemainingQty",
          dataType: "number",
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          width: ColumnWidth.Quantity4DecimalPoints.width,
          format: "##,###,###,###,##0.0000",
          cellTemplate: "Decimal",
          allowEditing: false,
        },
        {
          caption: "Issued Qty",
          dataField: "IssuedQty",
          dataType: "number",
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          width: ColumnWidth.Quantity4DecimalPoints.width,
          format: "##,###,###,###,##0.0000",
          cssClass: "edit-cell-numberbox-field",
          cellTemplate: "Decimal",
          allowSorting: false,
          editorOptions: {
            format: "##,###,###,###,##0.0000",
            onFocusIn: (e) => {
              if (e && e.event) {
                e.event.target.select();
              }
              this.getDataGridRef.option("focusedRowIndex", -1);
            },
            onKeyDown: this.onBatchIssuedQtyKeyDown,
            // valueChangeEvent: "input",
            onValueChanged: async (e, lineData) => {
              const data = lineData.data;
              const batchDataGrid = this.getBatchGridInstance(data.ParentID);
              const rowIndex = batchDataGrid.getRowIndexByKey(data.id);

              if (rowIndex !== -1) {
                let StockUMQty = e.value;

                await this.getDataGridRef
                  .byKey(data.ParentID)
                  .then(async (parentLine) => {
                    const newQty = (
                      parentLine.StockUMQty +
                      StockUMQty -
                      e.previousValue
                    ).roundNumber(4);

                    if (
                      newQty > parentLine.OutstandingQty &&
                      StockUMQty <= data.RemainingQty
                    ) {
                      // Handle the case where the total exceeds the outstanding quantity
                      const confirmQty = await this.$confirm(
                        batchOutstandingExceedMsg,
                        "Confirmation"
                      );

                      if (!confirmQty) {
                        StockUMQty = 0;
                        setTimeout(() => {
                          this.focusOnBatchEditableCell(data, "IssuedQty");
                        }, 100);
                      } else {
                        setTimeout(() => {
                          this.focusOnBatchEditableCell(data, "IssuedQty");
                          setTimeout(() => {
                            this.$nextTick(() => {
                              e.event.key = "Tab";
                              e.event.shiftKey = false;
                              this.onBatchIssuedQtyKeyDown(e, lineData);
                            });
                          }, 100);
                        }, 100);
                      }
                    }

                    batchDataGrid.cellValue(rowIndex, "IssuedQty", StockUMQty);
                  });
              }
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            if (
              value !== null &&
              value &&
              value > currentRowData.RemainingQty
            ) {
              await this.displayLineCellError(
                `${
                  currentRowData.BatchNo
                } has an issued quantity of ${formatNumber(
                  value,
                  "##,###,###,###,##0.0000"
                )}, which is greater than the batch remaining quantity of ${formatNumber(
                  currentRowData.RemainingQty,
                  "##,###,###,###,##0.0000"
                )}.
                Please update the batch allocated quantity so that it is less than or equal to ${formatNumber(
                  currentRowData.RemainingQty,
                  "##,###,###,###,##0.0000"
                )}.`
              );

              newData.IssuedQty = currentRowData.IssuedQty;
              this.getDataGridRef
                .byKey(currentRowData.ParentID)
                .then((rowInfo) => {
                  if (rowInfo) {
                    rowInfo.ChildDataSource.update(currentRowData.id, {
                      IssuedQty: currentRowData.IssuedQty,
                    });
                  }

                  const rowIndex = this.getBatchGridInstance(
                    currentRowData.ParentID
                  ).getRowIndexByKey(currentRowData.id);
                  if (rowIndex !== -1) {
                    this.getBatchGridInstance(
                      currentRowData.ParentID
                    ).cellValue(
                      rowIndex,
                      "IssuedQty",
                      currentRowData.IssuedQty
                    );
                  }
                });

              await this.$nextTick();

              setTimeout(() => {
                this.focusOnBatchEditableCell(currentRowData, "IssuedQty");
              }, 100);
            } else {
              this.getDataGridRef
                .byKey(currentRowData.ParentID)
                .then(async (rowInfo) => {
                  if (rowInfo) {
                    rowInfo.StockUMQty =
                      rowInfo.StockUMQty - currentRowData.IssuedQty + value;

                    await this.$nextTick();
                    setTimeout(() => {
                      this.workOrderMaterialDataSource
                        .update(currentRowData.ParentID, {
                          StockUMQty: rowInfo.StockUMQty,
                        })
                        .then(() => {
                          if (rowInfo.StockUMQty !== 0) {
                            this.getDataGridRef.selectRows([rowInfo.id], true);
                          } else {
                            this.getDataGridRef.deselectRows([rowInfo.id]);
                          }
                          this.getDataGridRef.refresh(true);
                        });
                    }, 1);
                  }
                });

              const items = await this.workOrderMaterialDataSource.load({
                filter: [
                  ["ProductCode", "=", currentRowData.ItemCode],
                  "and",
                  ["id", "<>", currentRowData.ParentID],
                ],
              });

              if (items.length > 0) {
                for (const item of items) {
                  if (item.IsBatchProduct != "No") {
                    const batchItems = await item.ChildDataSource.load({
                      filter: [
                        ["BatchNo", "=", currentRowData.BatchNo],
                        "and",
                        ["SupplierCode", "=", currentRowData.SupplierCode],
                      ],
                    });

                    if (batchItems.length > 0) {
                      item.ChildDataSource.update(batchItems[0].id, {
                        InTransactionQty:
                          batchItems[0].InTransactionQty != 0
                            ? batchItems[0].InTransactionQty -
                              currentRowData.IssuedQty +
                              value
                            : value,
                        RemainingQty:
                          batchItems[0].RemainingQty +
                          currentRowData.IssuedQty -
                          value,
                      });
                      await this.$nextTick();
                    }
                  }
                }
              }

              newData.IssuedQty = value;
            }

            const batchInstance = this.getBatchGridInstance(
              currentRowData.ParentID
            );
            if (batchInstance) {
              if (newData.IssuedQty !== 0)
                batchInstance.selectRows([currentRowData.id], true);
              else {
                batchInstance.deselectRows([currentRowData.id]);
              }
            }

            newData.IsIncluded = newData.IssuedQty !== 0;

            return newData;
          },
          allowEditing: true,
        },
      ];
    },
  },

  watch: {
    getHotKeySave: {
      handler(val) {
        if (val) {
          this.$store.dispatch("hotKey/setHotKeySave", false);
          this.onSaveProductionRelease();
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.gc-popup-release-and-issue-bulk {
  .gc-popup-form-content {
    background-color: white;
    padding: 12px;

    .gc-conversion-field .dx-numberbox .dx-texteditor-input {
      text-align: left !important;
    }
  }

  .data-grid-details {
    padding: 0px 12px 12px;

    .gc-remove-selection {
      pointer-events: none !important;
    }
  }

  .dx-datagrid
    .dx-datagrid-content
    .dx-datagrid-table
    .dx-row
    .dx-command-select.dx-editor-cell {
    padding-left: 5px !important;
    padding-right: 5px !important;
  }

  .dx-datagrid
    .dx-datagrid-content
    .dx-datagrid-table
    .dx-row
    .dx-editor-cell.dx-focused {
    padding-right: 5px !important;
    padding-left: 5px !important;
  }
}
</style>
